import { RADAR_STALE_MS } from "@divespots/contracts";

/** One position of the time bar: a measured radar frame or a forecast time. */
export interface BarStep {
  t: number;
  kind: "radar" | "forecast";
}

/** The forecast part of the bar: the wind bar's steps (half hours) or `precipTimeRange` hours. */
export interface ForecastAxis {
  min: number;
  max: number;
  step: number;
}

/** The newest frame still counts as "now" while it is no older than RADAR_STALE_MS. */
export const radarFresh = (newestMs: number, nowMs: number): boolean =>
  nowMs - newestMs <= RADAR_STALE_MS;

/**
 * The frame times the bar may use: all of them while the newest is fresh, none otherwise (a stale
 * radar counts as unavailable; "Jetzt" never labels an old frame).
 */
export function usableFrames(frames: readonly string[] | undefined, nowMs: number): number[] {
  if (!frames || frames.length === 0) return [];
  const ms = frames.map((f) => Date.parse(f));
  return radarFresh(ms[ms.length - 1]!, nowMs) ? ms : [];
}

export interface BarSteps {
  steps: BarStep[];
  /** Index of the newest frame ("Jetzt · gemessen"), -1 without frames. */
  jetzt: number;
  /** Index of the first forecast step, -1 without any (radar steps only). */
  divider: number;
}

/**
 * Radar steps are exactly the frames; the forecast steps follow from the first one strictly AFTER
 * the newest frame. Forecast down (`null`): radar steps only.
 */
export function buildSteps(frames: readonly number[], forecast: ForecastAxis | null): BarSteps {
  const steps: BarStep[] = frames.map((t) => ({ t, kind: "radar" }));
  const newest = frames.length > 0 ? frames[frames.length - 1]! : -Infinity;
  const jetzt = frames.length - 1;
  let divider = -1;
  if (forecast) {
    for (let t = forecast.min; t <= forecast.max; t += forecast.step) {
      if (t <= newest) continue;
      if (divider < 0) divider = steps.length;
      steps.push({ t, kind: "forecast" });
    }
  }
  return { steps, jetzt, divider };
}

/** The picked time's step (nearest when it left the list); no pick = the "Jetzt" step. */
export function selectStep(
  steps: readonly BarStep[],
  picked: number | null,
  jetzt: number,
): number {
  if (steps.length === 0) return -1;
  if (picked === null) return Math.max(0, jetzt);
  let best = 0;
  for (let i = 1; i < steps.length; i++)
    if (Math.abs(steps[i]!.t - picked) < Math.abs(steps[best]!.t - picked)) best = i;
  return best;
}
